import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import GoogleAuthButton from '../../components/GoogleAuthButton';
import VideoTutorialModal from '../../components/VideoTutorialModal';
import AgeGate from '../../components/AgeGate';
import SignupDisclaimerModal from '../../components/SignupDisclaimerModal';
import MobileOnlyGate from '../../components/MobileOnlyGate';
import googleAnalytics from '../../services/googleAnalytics';
import '../../styles/logo-animation.css';
import investingLogo from '../../assets/images/mainlogo.png';
import { useIsMobile } from '../../hooks/useIsMobile';

const SignUpPage = () => {
  const navigate = useNavigate();
  const { register, user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    dateOfBirth: ''
  });
  const [errors, setErrors] = useState({});
  const [errorType, setErrorType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLogoAnimation, setShowLogoAnimation] = useState(true);
  const isMobile = useIsMobile();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showAgeGate, setShowAgeGate] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [disclaimerData, setDisclaimerData] = useState(null);

  useEffect(() => {
    // Hide the logo animation after 2 seconds
    const timer = setTimeout(() => {
      setShowLogoAnimation(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);


  // Show mobile-only gate on desktop
  if (!isMobile) {
    return <MobileOnlyGate type="signup" />;
  }

  // Clean form styling helper
  const getFormFieldStyle = (fieldName) => ({
    formGroup: { marginBottom: '1.25rem' },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontWeight: '800',
      color: '#000000',
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      letterSpacing: '0.1em'
    },
    input: {
      width: '100%',
      padding: '14px 16px',
      background: '#FFFFFF',
      color: '#000000',
      border: errors[fieldName] ? '2px solid #D72638' : '2px solid #000000',
      borderRadius: '0',
      fontSize: '16px',
      fontWeight: '600',
      boxShadow: '3px 3px 0px rgba(0,0,0,0.1)',
      outline: 'none',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
    },
    error: {
      color: '#D72638',
      fontSize: '0.75rem',
      fontWeight: '600',
      marginTop: '4px',
      display: 'block'
    }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Clear error type when user starts typing
    if (errorType) {
      setErrorType(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username) newErrors.username = 'Username is required';
    else if (formData.username.length < 3) newErrors.username = 'Username must be at least 3 characters';
    else if (formData.username.length > 20) newErrors.username = 'Username must be 20 characters or less';
    else if (formData.username.includes('@')) newErrors.username = 'Username cannot be an email address';
    
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVideoModalClose = () => {
    setShowVideoModal(false);
    // Set flag to show trade popup after new user signup
    sessionStorage.setItem('justCompletedSignup', 'true');
    navigate('/onboarding');
  };

  const handleAgeGateConfirm = () => {
    setShowAgeGate(false);
  };

  const handleAgeGateDeny = () => {
    alert('You must be 16 years or older to use this service.');
    navigate('/');
  };

  const handleDisclaimerAccept = (data) => {
    setDisclaimerData(data);
    setShowDisclaimerModal(false);
    // Now proceed with actual registration
    performRegistration();
  };

  const handleDisclaimerCancel = () => {
    setShowDisclaimerModal(false);
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Show disclaimer modal instead of immediately registering
    setLoading(true);
    setShowDisclaimerModal(true);
  };

  const performRegistration = async () => {
    try {
      const { confirmPassword, ...registrationData } = formData;

      // Add disclaimer acceptance data to registration
      const fullRegistrationData = {
        ...registrationData,
        ...disclaimerData
      };

      console.log('Attempting registration with username:', fullRegistrationData.username);

      const result = await register(fullRegistrationData);
      console.log('Registration result:', JSON.stringify(result, null, 2));

      if (result.success) {
        // Track successful signup
        googleAnalytics.trackAuth.signup('email');
        googleAnalytics.trackConversion.signupComplete();

        // Show video tutorial modal before navigating to dashboard
        setShowVideoModal(true);
      } else {
        const errorMessage = result.error || result.message || 'Registration failed. Please try again.';
        const errorType = result.errorType || 'GENERIC_ERROR';
        console.error('Registration failed:', errorMessage, 'Type:', errorType);
        setErrors({ general: errorMessage });
        setErrorType(errorType);
      }
    } catch (error) {
      console.error('Registration error caught in SignUpPage:');
      console.error('- Full error object:', error);
      console.error('- Error message:', error.message);
      console.error('- Error status:', error.status);
      console.error('- Error response:', error.response);
      
      // Extract detailed error message
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.response?.data) {
        console.error('- Server error data:', JSON.stringify(error.response.data, null, 2));
        const errorData = error.response.data;
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.error('- Final error message to user:', errorMessage);
      setErrors({ general: errorMessage });
      setErrorType(error.errorType || 'GENERIC_ERROR');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Logo Popup Animation */}
      {showLogoAnimation && (
        <div className="logo-popup-overlay">
          <div className="logo-popup-container">
            <img 
              src={investingLogo} 
              alt="Kids Investment Game" 
              className="logo-popup-image"
            />
          </div>
        </div>
      )}

      <div style={{
      minHeight: '100vh',
      background: '#000000',
      color: '#FFFFFF',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
      position: 'relative'
    }}>
      
      <div style={{
        maxWidth: '440px',
        width: '100%',
        background: '#FFFFFF',
        border: '2px solid #000000',
        borderRadius: '0',
        boxShadow: '6px 6px 0px #000000',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          background: '#000000',
          color: '#FFFFFF',
          padding: '1.5rem 2rem',
          textAlign: 'center'
        }}>
          <img
            src={investingLogo}
            alt="Gideon the Bull"
            style={{
              display: 'inline-block',
              width: '84px',
              height: '84px',
              objectFit: 'contain',
              background: '#FFFFFF',
              border: '3px solid #000000',
              boxShadow: '4px 4px 0px #333333',
              padding: '6px',
              marginBottom: '0.75rem'
            }}
          />
          <h1 style={{
            margin: '0 0 0.5rem 0',
            fontSize: '1.75rem',
            fontWeight: '800',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>Create Account</h1>
          <p style={{
            margin: '0',
            fontSize: '0.9rem',
            fontWeight: '500',
            opacity: '0.85'
          }}>Join the investment learning platform</p>
        </div>

          {/* Authentication Status Message */}
          {isAuthenticated && user ? (
            <div style={{
              margin: '0',
              padding: '16px 20px',
              background: '#28a745',
              color: '#FFFFFF',
              textAlign: 'center'
            }}>
              <p style={{
                margin: '0 0 12px 0',
                fontSize: '0.95rem',
                fontWeight: '600'
              }}>
                Welcome back, {user.firstName || user.username}! You're already logged in.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  padding: '10px 20px',
                  background: '#FFFFFF',
                  color: '#28a745',
                  border: '2px solid #FFFFFF',
                  borderRadius: '0',
                  fontSize: '14px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Go to Dashboard
              </button>
            </div>
          ) : (
            <div style={{
              margin: '0',
              padding: '16px 20px',
              background: '#2563EB',
              color: '#FFFFFF',
              textAlign: 'center'
            }}>
              <p style={{
                margin: '0 0 6px 0',
                fontSize: '0.95rem',
                fontWeight: '600'
              }}>
                Learn to Invest in a Safe Environment
              </p>
              <p style={{
                margin: '0',
                fontSize: '0.8rem',
                fontWeight: '500',
                opacity: '0.9'
              }}>
                No Real Money • Real Market Data • AI-Powered Guide
              </p>
            </div>
          )}

          {/* Account Already Exists Error Message */}
          {errorType === 'ACCOUNT_EXISTS' && (
            <div style={{
              margin: '16px',
              padding: '16px',
              background: '#D72638',
              color: '#FFFFFF',
              textAlign: 'center'
            }}>
              <p style={{
                margin: '0 0 12px 0',
                fontSize: '0.9rem',
                fontWeight: '600'
              }}>
                This email or username is already registered.
              </p>
              <Link to="/login" style={{
                display: 'inline-block',
                padding: '10px 20px',
                background: '#FFFFFF',
                color: '#D72638',
                border: 'none',
                borderRadius: '0',
                fontSize: '14px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                textDecoration: 'none',
                transition: 'all 0.2s ease'
              }}>
                Sign In Instead
              </Link>
            </div>
          )}

          {/* Google Sign Up Button */}
          <div style={{ padding: '1.5rem 1.5rem 0 1.5rem' }}>
            <GoogleAuthButton
              mode="signup"
              buttonText="Sign up with Google"
              onSuccess={(user) => {
                console.log('Google Sign Up successful:', user);
              }}
              onError={(error) => {
                console.error('Google Sign Up error:', error);
                setErrors({ general: 'Google Sign Up failed. Please try again.' });
              }}
              style={{
                button: {
                  marginBottom: '0.75rem'
                }
              }}
            />

            {/* Divider */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              margin: '0.75rem 0 1rem 0'
            }}>
              <div style={{
                flex: 1,
                height: '1px',
                background: '#E5E5E5'
              }}></div>
              <span style={{
                padding: '0 1rem',
                color: '#666666',
                fontWeight: '600',
                fontSize: '12px',
                textTransform: 'uppercase'
              }}>or</span>
              <div style={{
                flex: 1,
                height: '1px',
                background: '#E5E5E5'
              }}></div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} style={{
            padding: '1.5rem',
            background: '#FFFFFF'
          }}>
            {errors.general && (
              <div style={{
                background: '#FEF2F2',
                color: '#DC2626',
                border: '1px solid #FECACA',
                padding: '12px 16px',
                marginBottom: '16px',
                borderRadius: '0',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                {errors.general}
              </div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={getFormFieldStyle('firstName').formGroup}>
                <label htmlFor="firstName" style={getFormFieldStyle('firstName').label}>First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="John"
                  style={getFormFieldStyle('firstName').input}
                />
                {errors.firstName && (
                  <span style={getFormFieldStyle('firstName').error}>{errors.firstName}</span>
                )}
              </div>

              <div style={getFormFieldStyle('lastName').formGroup}>
                <label htmlFor="lastName" style={getFormFieldStyle('lastName').label}>Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Doe"
                  style={getFormFieldStyle('lastName').input}
                />
                {errors.lastName && (
                  <span style={getFormFieldStyle('lastName').error}>{errors.lastName}</span>
                )}
              </div>
            </div>

            <div style={getFormFieldStyle('dateOfBirth').formGroup}>
              <label htmlFor="dateOfBirth" style={getFormFieldStyle('dateOfBirth').label}>Date of Birth</label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                style={getFormFieldStyle('dateOfBirth').input}
              />
              {errors.dateOfBirth && (
                <span style={getFormFieldStyle('dateOfBirth').error}>{errors.dateOfBirth}</span>
              )}
            </div>

            <div style={getFormFieldStyle('username').formGroup}>
              <label htmlFor="username" style={getFormFieldStyle('username').label}>Username</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="Choose a username (3-20 characters)"
                style={getFormFieldStyle('username').input}
                maxLength="20"
              />
              {errors.username && (
                <span style={getFormFieldStyle('username').error}>{errors.username}</span>
              )}
              <small style={{ color: '#888888', fontSize: '0.7rem', fontWeight: '500', marginTop: '4px', display: 'block' }}>
                3-20 characters, no email addresses
              </small>
            </div>

            <div style={getFormFieldStyle('email').formGroup}>
              <label htmlFor="email" style={getFormFieldStyle('email').label}>Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                style={getFormFieldStyle('email').input}
              />
              {errors.email && (
                <span style={getFormFieldStyle('email').error}>{errors.email}</span>
              )}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={getFormFieldStyle('password').formGroup}>
                <label htmlFor="password" style={getFormFieldStyle('password').label}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create password"
                    style={{
                      ...getFormFieldStyle('password').input,
                      paddingRight: '60px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      color: '#999999',
                      border: 'none',
                      padding: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px'
                    }}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {errors.password && (
                  <span style={getFormFieldStyle('password').error}>{errors.password}</span>
                )}
              </div>

              <div style={getFormFieldStyle('confirmPassword').formGroup}>
                <label htmlFor="confirmPassword" style={getFormFieldStyle('confirmPassword').label}>Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm password"
                    style={{
                      ...getFormFieldStyle('confirmPassword').input,
                      paddingRight: '60px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      color: '#999999',
                      border: 'none',
                      padding: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px'
                    }}
                  >
                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span style={getFormFieldStyle('confirmPassword').error}>{errors.confirmPassword}</span>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px 24px',
                background: '#000000',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '0',
                fontSize: '15px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '4px 4px 0px #000000',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.7 : 1,
                marginTop: '0.5rem'
              }}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <div style={{
            textAlign: 'center',
            padding: '1.25rem 1.5rem',
            background: '#F5F5F5',
            borderTop: '1px solid #E5E5E5'
          }}>
            <p style={{
              margin: '0',
              fontSize: '0.9rem',
              color: '#666666'
            }}>
              Already have an account?{' '}
              <Link to="/login" style={{
                color: '#000000',
                fontWeight: '700',
                textDecoration: 'none'
              }}>
                Sign In
              </Link>
            </p>
          </div>
      </div>
    </div>

    {/* Age Gate */}
    {showAgeGate && (
      <AgeGate
        onConfirm={handleAgeGateConfirm}
        onDeny={handleAgeGateDeny}
        isMobile={isMobile}
      />
    )}

    {/* Disclaimer Modal */}
    {showDisclaimerModal && (
      <SignupDisclaimerModal
        onAccept={handleDisclaimerAccept}
        onCancel={handleDisclaimerCancel}
        isMobile={isMobile}
      />
    )}

    {/* Video Tutorial Modal */}
    <VideoTutorialModal
      isOpen={showVideoModal}
      onClose={handleVideoModalClose}
    />
    </>
  );
};

export default SignUpPage;